import React, { useMemo } from 'react';
import {
  SidebarNavTitle,
  StyledBottomSidebarNavItem,
  StyledHrefLink,
  StyledLink,
  StyledSidebarNavDropdown,
  StyledSidebarNavItem
} from './SidebarComponents';
import { closeActiveModal, openModal } from '../../helper/modals';
import {
  MdAnalytics,
  MdBookmarkAdded,
  MdEvent,
  MdOutlineSettings,
  MdSpaceDashboard,
  MdThermostat
} from 'react-icons/md';
import { BsBuildingsFill } from 'react-icons/bs';
import { FaCar, FaIntercom, FaKey, FaTools, FaTruck, FaUsers, FaWater, FaCreditCard } from 'react-icons/fa';
import { LuArrowUpRight, LuNewspaper } from 'react-icons/lu';
import { IoIosHelpCircle } from 'react-icons/io';
import { IoDocumentTextOutline } from 'react-icons/io5';
import { SiGoogleanalytics } from 'react-icons/si';
import { VscOrganization } from 'react-icons/vsc';
import { BiSolidCoupon } from 'react-icons/bi';
import { PiVideoCamera, PiVideoCameraFill } from 'react-icons/pi';
import { CSidebarNavDivider } from '@coreui/react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { changeOpenedModal } from '../../redux/actions/headerModal';
import { whiteListedDomains } from '../../helper/constants';
import { useHistory, useLocation } from 'react-router-dom';
import { logout as logoutAction } from '../../redux/actions/auth';
import { isManagerRole, Projects, UserRole } from '../../views/users/constant';
import styled from 'styled-components';
import { RiParkingBoxLine } from 'react-icons/ri';
import { useGetConfigs } from '../../hooks/useGetConfigs';
import { useGetWLDConfigs } from '../../hooks/useGetWLDConfigs';
import SupportIcon from '../../assets/icons/sidebar/support.svg';

const ProjectID = process.env.REACT_APP_PROJECT_ID;
const DevProjectID = 'gratasys-dev';
// PROD: Charlesgate 51, Grata 66, Maclab - Eleanor 87, Maclab - Laurent 86, Zen - The parks 88
// DEV : 1 Grata,
export const ALLOWED_SECURITY_ORGS = {
  'gratasys-dev': [1, 246],
  gratasys: [51, 66, 87, 86, 88],
  'gratasys-dev-cameras': [],
  'gratasys-cameras': [87, 86, 88]
};

/* 
Action Types: link, dropdown, action
  link: Link to redirect user
  dropdown: Navigation dropdown, leads to other links (news)
  action: Click action - Open modals, logout
*/
const DropdownSidebarComponent = ({ title, icon, children, location }) => {
  return (
    <StyledSidebarNavDropdown name={title} icon={icon}>
      {children.map((child) => {
        return (
          <StyledLink key={child.title} to={child.to}>
            <StyledSidebarNavItem selected={location.pathname === child.to}>
              {child.title}
            </StyledSidebarNavItem>
          </StyledLink>
        );
      })}
    </StyledSidebarNavDropdown>
  );
};

const LinkSidebarComponent = ({ title, icon, to, onClick, location }) => {
  return (
    <StyledLink
      to={to}
      onClick={() => {
        closeActiveModal();
        if (onClick) onClick();
      }}>
      <StyledSidebarNavItem selected={location.pathname === to}>
        {icon} {title}
      </StyledSidebarNavItem>
    </StyledLink>
  );
};

const HrefLinkSidebarComponent = ({ title, icon, to }) => {
  return (
    <a href={to} target="_blank" rel="noopener noreferrer">
      <StyledSidebarNavItem>
        {icon} {title} <LuArrowUpRight size={13} />
      </StyledSidebarNavItem>
    </a>
  )
}

const ActionSidebarComponent = ({ title, icon, onClick }) => {
  return (
    <a onClick={onClick}>
      <StyledSidebarNavItem>
        {icon} {title}
      </StyledSidebarNavItem>
    </a>
  );
};

const BottomActionSidebarComponent = ({ title, icon, to, onClick }) => {
  return (
    <a onClick={onClick} href={to} target="_blank" rel="noopener noreferrer">
      <StyledBottomSidebarNavItem>
        {title} {icon}
      </StyledBottomSidebarNavItem>
    </a>
  );
};

const renderNavItem = ({ navigationItem, ...rest }) => {
  switch (navigationItem.type) {
    case 'link':
      return <LinkSidebarComponent {...navigationItem} {...rest} />;
    case 'action':
      return <ActionSidebarComponent {...navigationItem} {...rest} />;
    case 'dropdown':
      return <DropdownSidebarComponent {...navigationItem} {...rest} />;
    case 'href-link':
      return <HrefLinkSidebarComponent {...navigationItem} {...rest} />;
  }
};

export const DefaultSidebar = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { roleId } = useSelector((state) => state.auth);
  const { profile } = useSelector((state) => state.profile);
  const { globalOrgId } = useSelector((state) => state.Organization);
  const { project } = useSelector((state) => state.country);
  const { config: buildingConfig, orgConfig } = useGetConfigs();
  const { shouldShowLeakDetectors } = useGetWLDConfigs();

  const openHelpModal = () => {
    dispatch(changeOpenedModal({ modal: 'helpModal' }));
  };

  const removeLogoutItems = () => {
    localStorage.clear();
    history.push('/login');
    window.location.reload();
  };

  const logout = () => {
    dispatch(
      logoutAction({
        success: () => {
          removeLogoutItems();
        },
        fail: () => {
          removeLogoutItems();
        }
      })
    );
  };

  const navigation = useMemo(() => {
    const buildingsTabs = [];

    if (roleId === UserRole.SUPER_ADMIN) {
      buildingsTabs.push({
        type: 'link',
        title: t('Organizations'),
        to: '/organization',
        icon: <VscOrganization size={23} />
      });
    }

    buildingsTabs.push({
      type: 'link',
      title: t('My Buildings'),
      to: '/buildings',
      icon: <BsBuildingsFill size={23} />
    });

    buildingsTabs.push({
      type: 'link',
      title: t('Users'),
      to: '/users',
      icon: <FaUsers size={23} />
    });

    if (
      roleId === UserRole.SUPER_ADMIN ||
      buildingConfig?.enable_security_cameras ||
      buildingConfig?.enable_ai_nvr
    ) {
      if (buildingConfig?.enable_ai_nvr && isManagerRole(roleId)) {
        buildingsTabs.push({
          type: 'dropdown',
          title: t('Security'),
          icon: <PiVideoCamera size={23} />,
          children: [
            {
              title: t('Cameras'),
              to: '/security'
            },
            {
              title: t('NVR'),
              to: '/security/nvr'
            }
          ]
        });
      } else {
        buildingsTabs.push({
          type: 'link',
          title: t('Security'),
          to: '/security',
          icon: <PiVideoCamera size={23} />
        });
      }
    }

    buildingsTabs.push({
      type: 'link',
      title: t('Work Requests'),
      to: '/requests',
      icon: <FaTools size={23} />
    });

    buildingsTabs.push({
      type: 'dropdown',
      title: t('News'),
      icon: <LuNewspaper size={23} />,
      children: [
        {
          title: t('Templates'),
          to: '/conversation/templates'
        },
        {
          title: t('Drafts'),
          to: '/conversation/drafts'
        },
        {
          title: t('Sent'),
          to: '/conversation/sent'
        }
      ]
    });

    if (roleId === UserRole.SUPER_ADMIN || orgConfig?.enable_deliveries) {
      buildingsTabs.push({
        type: 'link',
        title: t('Deliveries'),
        to: '/deliveries',
        icon: <FaTruck size={23} />
      });
    }

    if (
      roleId === UserRole.SUPER_ADMIN ||
      orgConfig?.enable_document_storage ||
      buildingConfig?.enable_document_storage
    ) {
      buildingsTabs.push({
        type: 'link',
        title: t('Documents'),
        to: '/documents',
        icon: <IoDocumentTextOutline size={23} />
      });
    }

    if (
      isManagerRole(roleId) ||
      orgConfig?.enable_events ||
      buildingConfig?.enable_events
    ) {
      buildingsTabs.push({
        type: 'link',
        title: t('Events'),
        to: '/events',
        icon: <MdEvent size={23} />
      });
    }

    if (
      roleId === UserRole.SUPER_ADMIN ||
      (orgConfig?.enable_vopay &&
        (roleId === UserRole.ADMIN || roleId === UserRole.MANAGER))
    ) {
      buildingsTabs.push({
        type: 'link',
        title: t('Payments'),
        to: '/payments',
        icon: <FaCreditCard size={23} />
      });
    }

    buildingsTabs.push({
      type: 'link',
      title: t('Bookings'),
      to: '/bookings',
      icon: <MdBookmarkAdded size={23} />
    });

    if (roleId === UserRole.SUPER_ADMIN || shouldShowLeakDetectors) {
      buildingsTabs.push({
        type: 'link',
        title: t('Leak Detectors'),
        to: '/leak-detectors',
        icon: <FaWater size={23} />
      });
    }

    if (roleId === UserRole.SUPER_ADMIN || orgConfig?.enable_thermostats) {
      buildingsTabs.push({
        type: 'link',
        title: t('Thermostats'),
        to: '/thermostats',
        icon: <MdThermostat size={23} />
      });
    }

    if (
      roleId === UserRole.SUPER_ADMIN ||
      ((orgConfig?.enable_akuvox || buildingConfig?.enable_akuvox) &&
        (roleId === UserRole.ADMIN || roleId === UserRole.MANAGER))
    ) {
      buildingsTabs.push({
        type: 'link',
        title: t('Intercom'),
        to: '/intercom',
        icon: <FaIntercom size={23} />
      });
    }

    if (
      roleId === UserRole.SUPER_ADMIN ||
      (orgConfig?.enable_license_plates &&
        (roleId === UserRole.ADMIN || roleId === UserRole.MANAGER))
    ) {
      buildingsTabs.push({
        type: 'link',
        title: t('Parking'),
        to: '/parking',
        icon: <RiParkingBoxLine size={23} />
      });
    }

    const reportsTabs = [
      {
        type: 'link',
        title: t('Building Analytics'),
        to: '/reports',
        icon: <MdAnalytics size={23} />
      }
    ];

    if (
      roleId === UserRole.SUPER_ADMIN ||
      (orgConfig?.enable_license_plates &&
        (roleId === UserRole.ADMIN || roleId === UserRole.MANAGER))
    ) {
      reportsTabs.push({
        type: 'link',
        title: t('Parking analytics'),
        to: '/parking-analytics',
        icon: <FaCar size={23} />
      });
    }

    if (roleId === UserRole.SUPER_ADMIN || roleId === UserRole.ADMIN) {
      reportsTabs.push({
        type: 'link',
        title: t('Staff Analytics'),
        to: '/analytics',
        icon: <SiGoogleanalytics size={23} />
      });
    }

    if (roleId === UserRole.SUPER_ADMIN) {
      reportsTabs.push({
        type: 'link',
        title: t('Grata Plus'),
        to: '/deals',
        icon: <BiSolidCoupon size={23} />
      });
    }

    return [
      {
        title: '',
        tabs: [
          {
            type: 'link',
            title: t('Dashboard'),
            to: '/dashboard',
            icon: <MdSpaceDashboard size={23} />
          }
        ]
      },
      {
        title: 'My Buildings',
        tabs: buildingsTabs
      },
      {
        title: 'Reports',
        tabs: reportsTabs
      },
      {
        title: 'Settings',
        tabs: [
          {
            type: 'link',
            title: t('My Profile'),
            to: '/settings',
            icon: <MdOutlineSettings size={23} />
          },
          {
            type: 'href-link',
            title: t('Help Centre'),
            to: 'https://help.grata.life/property-managers',
            icon: <IoIosHelpCircle size={23} />
          },
          {
            type: 'href-link',
            title: t('Support'),
            to: 'mailto:support@grata.life',
            icon: <StyledNavIcon src={SupportIcon} alt="" />
          }
        ]
      }
    ];
  }, [roleId, globalOrgId, profile?.email, buildingConfig, orgConfig, shouldShowLeakDetectors]);

  const bottomNavigation = useMemo(() => {
    const navigation = [
      {
        type: 'href-link',
        title: t('Terms and Conditions'),
        to: 'https://www.grata.life/terms-and-conditions',
        icon: <LuArrowUpRight size={13} />
      },
      {
        type: 'href-link',
        title: t('Privacy Policy'),
        to: 'https://www.grata.life/privacy-policy',
        icon: <LuArrowUpRight size={13} />
      },
    ];

    if (roleId === UserRole.SUPER_ADMIN && ProjectID == DevProjectID) {
      navigation.push({
        type: 'action',
        title: t('Change Project'),
        onClick: () => {
          openModal('ModalEditProject');
        }
      });
    }

    if (roleId === UserRole.SUPER_ADMIN && project == Projects.GRATA) {
      navigation.push({
        type: 'action',
        title: t('Change Data Center'),
        onClick: () => {
          openModal('ModalEditDataCenter');
        }
      });
    }

    navigation.push({
      type: 'action',
      title: t('Logout'),
      onClick: () => {
        logout();
      }
    });
    return navigation;
  }, []);

  return (
    <>
      <SidebarNavigationContainer>
        {navigation.map((item) => {
          return (
            <div key={item.title}>
              <SidebarNavTitle>{item.title}</SidebarNavTitle>
              <div>
                {item.tabs.map((tab) => {
                  return (
                    <React.Fragment key={tab.title}>
                      {renderNavItem({
                        navigationItem: tab,
                        location
                      })}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          );
        })}
      </SidebarNavigationContainer>
      <CSidebarNavDivider />
      {bottomNavigation.map((item) => {
        return (
          <BottomActionSidebarComponent
            key={item.title}
            title={item.title}
            icon={item.icon}
            to={item.to}
            onClick={item.onClick}
          />
        );
      })}
    </>
  );
};

const SidebarNavigationContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const StyledNavIcon = styled.img`
  width: 23px;
`;
